import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './pos.css';

const API_URL = process.env.REACT_APP_API_URL || '';

export default function PuntoDeVenta() {
  const navigate = useNavigate();
  const [productos, setProductos] = useState([]);
  const [cart, setCart] = useState([]);
  const [busqueda, setBusqueda] = useState('');
  const [resultados, setResultados] = useState([]);
  const [cliente, setCliente] = useState({ nombre: 'CONSUMIDOR FINAL', ruc: '9999999999999', direccion: 'S/N', email: '' });
  const [formaPago, setFormaPago] = useState('01');
  const [efectivoRecibido, setEfectivoRecibido] = useState('');
  const [showCobrar, setShowCobrar] = useState(false);
  const [showCliente, setShowCliente] = useState(false);
  const [showEspera, setShowEspera] = useState(false);
  const [ventasEspera, setVentasEspera] = useState([]);
  const [procesando, setProcesando] = useState(false);
  const [mensaje, setMensaje] = useState({ text: '', tipo: '' });
  const [ultimaFactura, setUltimaFactura] = useState(null);
  const [config, setConfig] = useState({ establecimiento: '001', puntoEmision: '001' });
  const [empresa, setEmpresa] = useState({ nombre: '', ruc: '' });
  const [buscandoCli, setBuscandoCli] = useState(false);
  const [clienteEncontrado, setClienteEncontrado] = useState(false);
  const [clienteModal, setClienteModal] = useState({ nombre: '', ruc: '', direccion: '', email: '' });
  const [creditoInfo, setCreditoInfo] = useState(null); // { limite, disponible, saldoPendiente }
  const searchRef = useRef(null);
  const efectivoRef = useRef(null);

  const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  // Cargar datos iniciales
  useEffect(() => {
    cargarProductos();
    cargarConfig();
    // Focus en búsqueda
    searchRef.current?.focus();
  }, []);

  // Atajos de teclado
  useEffect(() => {
    const handler = (e) => {
      if (e.target.tagName === 'INPUT' && e.key !== 'Escape' && !e.key.startsWith('F')) return;
      
      switch (e.key) {
        case 'F1':
          e.preventDefault();
          searchRef.current?.focus();
          break;
        case 'F2':
          e.preventDefault();
          setClienteModal({ nombre: '', ruc: '', direccion: '', email: '' });
          setClienteEncontrado(false);
          setShowCliente(true);
          break;
        case 'F5':
          e.preventDefault();
          if (cart.length > 0) setShowCobrar(true);
          break;
        case 'F9':
          e.preventDefault();
          if (cart.length > 0) enviarAEspera();
          else setShowEspera(true);
          break;
        case 'F12':
          e.preventDefault();
          anularVenta();
          break;
        case 'Escape':
          setShowCobrar(false);
          setShowCliente(false);
          setShowEspera(false);
          searchRef.current?.focus();
          break;
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [cart]);

  const cargarProductos = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/productos?paraVenta=true`, { headers: headers() });
      setProductos(Array.isArray(res.data) ? res.data : []);
    } catch { setProductos([]); }
  };

  const cargarConfig = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/secuencial`, { headers: headers() });
      if (res.data) {
        setConfig({ establecimiento: res.data.establecimiento || '001', puntoEmision: res.data.ptoEmision || res.data.puntoEmision || '001' });
      }
      // Cargar info empresa del usuario
      try {
        const resEmp = await axios.get(`${API_URL}/api/empresa/configuracion`, { headers: headers() });
        const emp = resEmp.data?.empresa || {};
        setEmpresa({ nombre: emp.nombreComercial || emp.razonSocial || 'EMPRESA', ruc: emp.ruc || '' });
      } catch {}
      // Cargar configuración de stock
      try {
        const resConf = await axios.get(`${API_URL}/api/configuracion/contable`, { headers: headers() });
        if (resConf.data) {
          setPermitirSinStock(resConf.data.permitir_venta_sin_stock !== false);
          setRecibosHabilitado(resConf.data.recibos_sin_factura_habilitado === true);
        }
      } catch {}
      // Cargar configuración de impresora (compartida por empresa)
      try {
        const resPos = await axios.get(`${API_URL}/api/config-pos`, { headers: headers() });
        if (resPos.data?.data) {
          const temaLocal = localStorage.getItem('tema_pos') || 'tema-azul';
          const configCompleta = { ...resPos.data.data, tema_pos: temaLocal };
          localStorage.setItem('config_impresora', JSON.stringify(configCompleta));
        }
      } catch {}
    } catch {}
  };

  // Buscar cliente por cédula/RUC en la base de datos
  const buscarClientePorCedula = async (identificacion) => {
    setBuscandoCli(true);
    setClienteEncontrado(false);
    try {
      // Buscar en clientes registrados
      const res = await axios.get(`${API_URL}/api/clientes`, { headers: headers() });
      const clientes = Array.isArray(res.data) ? res.data : [];
      const encontrado = clientes.find(c => 
        (c.identificacion || c.ruc || '') === identificacion
      );

      if (encontrado) {
        setClienteModal({
          nombre: encontrado.razon_social || encontrado.nombre || '',
          ruc: identificacion,
          direccion: encontrado.direccion_1 || encontrado.direccion || 'S/N',
          email: (Array.isArray(encontrado.emails) && encontrado.emails[0]) || encontrado.email || '',
        });
        setClienteEncontrado(true);
      } else {
        // Si no está en la base local, intentar endpoint de búsqueda
        try {
          const sriRes = await axios.get(`${API_URL}/api/clientes/buscar/identificacion/${identificacion}`, { headers: headers() });
          if (sriRes.data && (sriRes.data.razon_social || sriRes.data.nombre)) {
            setClienteModal({
              nombre: sriRes.data.razon_social || sriRes.data.nombre || '',
              ruc: identificacion,
              direccion: sriRes.data.direccion_1 || sriRes.data.direccion || 'S/N',
              email: '',
            });
            setClienteEncontrado(true);
          }
        } catch {}
      }
    } catch {}
    setBuscandoCli(false);

    // Consultar crédito del cliente
    if (identificacion && identificacion !== '9999999999999') {
      try {
        const res = await axios.get(`${API_URL}/api/clientes`, { headers: headers() });
        const clientes = Array.isArray(res.data) ? res.data : [];
        const cli = clientes.find(c => (c.identificacion || c.ruc || '') === identificacion);
        if (cli && parseFloat(cli.limite_credito || 0) > 0) {
          // Tiene límite de crédito
          const limite = parseFloat(cli.limite_credito || 0);
          const saldoPendiente = parseFloat(cli.saldo_pendiente_real || 0);
          const disponible = limite - saldoPendiente;
          setCreditoInfo({ limite, disponible, saldoPendiente });
        } else {
          setCreditoInfo(null);
        }
      } catch { setCreditoInfo(null); }
    } else {
      setCreditoInfo(null);
    }
  };

  // Buscar productos
  useEffect(() => {
    if (busqueda.length >= 2) {
      const term = busqueda.toLowerCase();
      const found = productos.filter(p =>
        (p.codigo || '').toLowerCase().includes(term) ||
        (p.codigo_auxiliar_sku || '').toLowerCase().includes(term) ||
        (p.nombre || '').toLowerCase().includes(term)
      ).slice(0, 8);
      setResultados(found);
    } else {
      setResultados([]);
    }
  }, [busqueda, productos]);

  // Producto pendiente de agregar (esperando cantidad)
  const [productoParaAgregar, setProductoParaAgregar] = useState(null);
  const [cantidadInput, setCantidadInput] = useState('1');
  const [precioCustom, setPrecioCustom] = useState('');
  const [stockError, setStockError] = useState('');
  const [permitirSinStock, setPermitirSinStock] = useState(true);
  const [recibosHabilitado, setRecibosHabilitado] = useState(false);
  const cantidadRef = useRef(null);

  // Abrir popup de cantidad
  const solicitarCantidad = (producto) => {
    setProductoParaAgregar(producto);
    setCantidadInput('1');
    setPrecioCustom('');
    setStockError('');
    setBusqueda('');
    setResultados([]);
    setTimeout(() => cantidadRef.current?.select(), 100);
  };

  // Confirmar agregar al carrito con cantidad
  const confirmarAgregarProducto = () => {
    if (!productoParaAgregar) return;
    const cant = parseFloat(cantidadInput) || 1;
    if (cant <= 0) return;

    // Validar stock
    const stockDisponible = parseFloat(productoParaAgregar.cantidad_stock || productoParaAgregar.stock_actual || 0);
    // Considerar lo que ya está en el carrito
    const enCarrito = cart.find(c => c.codigo === productoParaAgregar.codigo);
    const yaEnCarrito = enCarrito ? enCarrito.cantidad : 0;
    const totalSolicitado = yaEnCarrito + cant;

    if (!permitirSinStock && stockDisponible < totalSolicitado) {
      setStockError(`❌ Stock insuficiente. Disponible: ${stockDisponible}, en carrito: ${yaEnCarrito}, solicitado: ${cant}`);
      return;
    }

    setStockError('');
    const existente = cart.findIndex(c => c.codigo === productoParaAgregar.codigo);
    if (existente >= 0) {
      const nuevo = [...cart];
      nuevo[existente].cantidad += cant;
      setCart(nuevo);
    } else {
      setCart([...cart, {
        codigo: productoParaAgregar.codigo || `P${Date.now()}`,
        nombre: productoParaAgregar.nombre,
        cantidad: cant,
        precio: productoParaAgregar.producto_especial && precioCustom ? parseFloat(precioCustom) : parseFloat(productoParaAgregar.pvp || productoParaAgregar.precio_1 || productoParaAgregar.precio_venta || 0),
        pvp: parseFloat(productoParaAgregar.pvp || productoParaAgregar.precio_1 || productoParaAgregar.precio_venta || 0),
        precio_2: parseFloat(productoParaAgregar.precio_2 || 0),
        precio_3: parseFloat(productoParaAgregar.precio_3 || 0),
        iva: productoParaAgregar.iva_porcentaje === '0%' ? '0%' : '15%',
        descuento: 0,
      }]);
    }
    setProductoParaAgregar(null);
    setCantidadInput('1');
    searchRef.current?.focus();
  };

  // Enter en búsqueda: si hay exactamente 1 resultado o match por código exacto
  const handleBusquedaKeyDown = (e) => {
    if (e.key === 'Enter' && busqueda.trim()) {
      const exact = productos.find(p =>
        (p.codigo || '').toLowerCase() === busqueda.toLowerCase() ||
        (p.codigo_auxiliar_sku || '').toLowerCase() === busqueda.toLowerCase()
      );
      if (exact) {
        solicitarCantidad(exact);
      } else if (resultados.length === 1) {
        solicitarCantidad(resultados[0]);
      }
    }
  };

  // Modificar cantidad
  const cambiarCantidad = (index, delta) => {
    const nuevo = [...cart];
    nuevo[index].cantidad += delta;
    if (nuevo[index].cantidad <= 0) nuevo.splice(index, 1);
    setCart(nuevo);
  };

  const eliminarItem = (index) => {
    setCart(cart.filter((_, i) => i !== index));
  };

  // Cálculos
  const subtotal0 = cart.filter(c => c.iva === '0%').reduce((s, c) => s + (c.cantidad * c.precio), 0);
  const subtotal15 = cart.filter(c => c.iva !== '0%').reduce((s, c) => s + (c.cantidad * c.precio), 0);
  const iva = subtotal15 * 0.15;
  const total = subtotal0 + subtotal15 + iva;
  const cambio = parseFloat(efectivoRecibido || 0) - total;

  // Emitir Recibo (sin factura SRI)
  const emitirRecibo = async () => {
    if (cart.length === 0) return;
    setProcesando(true);
    setMensaje({ text: '', tipo: '' });

    try {
      const payload = {
        cliente: {
          nombre: cliente.nombre || 'CONSUMIDOR FINAL',
          ruc: cliente.ruc || '9999999999999',
          direccion: cliente.direccion || 'S/N',
          email: cliente.email || '',
        },
        detalles: cart.map(c => ({
          codigo: c.codigo, nombre: c.nombre, cantidad: c.cantidad,
          precio: c.precio, iva: c.iva, descuento: c.cantidad * c.precio * (c.descuento / 100),
        })),
        totales: {
          subtotalSinImpuestos: subtotal0 + subtotal15,
          subtotal15, subtotal0, totalDescuento: 0, iva15: iva, valorPagar: total,
        },
        formaPago,
      };

      const res = await axios.post(`${API_URL}/api/recibos`, payload, { headers: headers() });
      if (res.data.success) {
        setMensaje({ text: `🧾 Recibo ${res.data.data.numero} emitido`, tipo: 'success' });

        // Imprimir ticket
        const configImpresora = JSON.parse(localStorage.getItem('config_impresora') || '{}');
        if (configImpresora.auto_imprimir_ticket) {
          imprimirTicketRecibo(payload, res.data.data, configImpresora);
        }

        setCart([]);
        setCliente({ nombre: 'CONSUMIDOR FINAL', ruc: '9999999999999', direccion: 'S/N', email: '' }); setCreditoInfo(null);
        setEfectivoRecibido('');
        setShowCobrar(false);
        setTimeout(() => { setMensaje({ text: '', tipo: '' }); searchRef.current?.focus(); }, 4000);
      } else {
        setMensaje({ text: `❌ ${res.data.message || 'Error'}`, tipo: 'error' });
      }
    } catch (err) {
      setMensaje({ text: `❌ ${err.response?.data?.message || err.message}`, tipo: 'error' });
    }
    setProcesando(false);
  };

  // Imprimir ticket de recibo (sin factura)
  const imprimirTicketRecibo = (payload, recibo, configImp) => {
    const tipo = configImp.tipo_ticket || '58mm';
    const ancho = tipo === '58mm' ? 32 : 48;
    const linea = '='.repeat(ancho);
    const lineaFina = '-'.repeat(ancho);
    const centrar = (t) => { const p = Math.max(0, Math.floor((ancho - t.length) / 2)); return ' '.repeat(p) + t; };
    const derecha = (lab, val) => `${lab}${' '.repeat(Math.max(1, ancho - lab.length - val.length))}${val}`;

    const items = payload.detalles.map(d => {
      const nombre = d.nombre.substring(0, ancho - 2);
      const totalItem = (d.cantidad * d.precio).toFixed(2);
      return `${nombre}\n  ${d.cantidad} x $${d.precio.toFixed(2)}${' '.repeat(Math.max(1, ancho - 6 - d.cantidad.toString().length - d.precio.toFixed(2).length - totalItem.length - 5))}$${totalItem}`;
    }).join('\n');

    const ticket = [
      centrar(configImp.nombre_empresa_ticket || empresa.nombre || 'EMPRESA'),
      empresa.ruc ? centrar(`RUC: ${empresa.ruc}`) : '',
      linea,
      centrar('RECIBO DE VENTA'),
      centrar(`No: ${recibo.numero}`),
      centrar(new Date().toLocaleString('es-EC')),
      linea,
      `Cliente: ${payload.cliente.nombre}`,
      payload.cliente.ruc !== '9999999999999' ? `CI/RUC:  ${payload.cliente.ruc}` : '',
      payload.cliente.direccion && payload.cliente.direccion !== 'S/N' ? `Dir:     ${payload.cliente.direccion}` : '',
      payload.cliente.email ? `Email:   ${payload.cliente.email}` : '',
      lineaFina,
      items,
      lineaFina,
      payload.totales.subtotal0 > 0 ? derecha('SUBTOTAL 0%:', `$${payload.totales.subtotal0.toFixed(2)}`) : '',
      payload.totales.subtotal15 > 0 ? derecha('SUBTOTAL 15%:', `$${payload.totales.subtotal15.toFixed(2)}`) : '',
      (payload.totales.subtotal0 === 0 || payload.totales.subtotal15 === 0)
        ? derecha('SUBTOTAL:', `$${(payload.totales.subtotal15 + payload.totales.subtotal0).toFixed(2)}`) : '',
      payload.totales.iva15 > 0 ? derecha('IVA 15%:', `$${payload.totales.iva15.toFixed(2)}`) : derecha('IVA:', '$0.00'),
      payload.totales.totalDescuento > 0 ? derecha('DESCUENTO:', `-$${payload.totales.totalDescuento.toFixed(2)}`) : '',
      linea,
      derecha('TOTAL:', `$${payload.totales.valorPagar.toFixed(2)}`),
      linea,
      efectivoRecibido ? derecha('EFECTIVO:', `$${parseFloat(efectivoRecibido).toFixed(2)}`) : '',
      efectivoRecibido ? derecha('CAMBIO:', `$${Math.max(0, parseFloat(efectivoRecibido) - payload.totales.valorPagar).toFixed(2)}`) : '',
      '',
      centrar('DOCUMENTO SIN VALOR TRIBUTARIO'),
      '',
      centrar(configImp.mensaje_pie_ticket || 'Gracias por su compra'),
      '',
    ].filter(Boolean).join('\n');

    abrirVentanaTicket(ticket, tipo, configImp);
  };

  // Función de impresión de ticket (FACTURA SRI)
  const imprimirTicket = (payload, facturaRes, configImp) => {
    const tipo = configImp.tipo_ticket || '58mm';
    const ancho = tipo === '58mm' ? 32 : 48;
    const linea = '='.repeat(ancho);
    const lineaFina = '-'.repeat(ancho);
    const centrar = (t) => { const p = Math.max(0, Math.floor((ancho - t.length) / 2)); return ' '.repeat(p) + t; };
    const derecha = (lab, val) => `${lab}${' '.repeat(Math.max(1, ancho - lab.length - val.length))}${val}`;

    const items = payload.detalles.map(d => {
      const nombre = d.nombre.substring(0, ancho - 2);
      const totalItem = (d.cantidad * d.precio).toFixed(2);
      return `${nombre}\n  ${d.cantidad} x $${d.precio.toFixed(2)}${' '.repeat(Math.max(1, ancho - 6 - d.cantidad.toString().length - d.precio.toFixed(2).length - totalItem.length - 5))}$${totalItem}`;
    }).join('\n');

    const secuencial = facturaRes.secuencial ? String(facturaRes.secuencial).padStart(9, '0') : '';
    const numFactura = secuencial ? `${config.establecimiento}-${config.puntoEmision}-${secuencial}` : '';
    const claveAcceso = facturaRes.claveAcceso || '';

    const ticket = [
      centrar(configImp.nombre_empresa_ticket || empresa.nombre || 'EMPRESA'),
      empresa.ruc ? centrar(`RUC: ${empresa.ruc}`) : '',
      linea,
      centrar('FACTURA ELECTRONICA'),
      numFactura ? centrar(`No: ${numFactura}`) : '',
      centrar(new Date().toLocaleString('es-EC')),
      linea,
      `Cliente: ${payload.cliente.nombre}`,
      payload.cliente.ruc !== '9999999999999' ? `CI/RUC:  ${payload.cliente.ruc}` : '',
      payload.cliente.direccion && payload.cliente.direccion !== 'S/N' ? `Dir:     ${payload.cliente.direccion}` : '',
      payload.cliente.email ? `Email:   ${payload.cliente.email}` : '',
      lineaFina,
      items,
      lineaFina,
      payload.totales.subtotal0 > 0 ? derecha('SUBTOTAL 0%:', `$${payload.totales.subtotal0.toFixed(2)}`) : '',
      payload.totales.subtotal15 > 0 ? derecha('SUBTOTAL 15%:', `$${payload.totales.subtotal15.toFixed(2)}`) : '',
      (payload.totales.subtotal0 === 0 || payload.totales.subtotal15 === 0)
        ? derecha('SUBTOTAL:', `$${(payload.totales.subtotal15 + payload.totales.subtotal0).toFixed(2)}`) : '',
      payload.totales.iva15 > 0 ? derecha('IVA 15%:', `$${payload.totales.iva15.toFixed(2)}`) : derecha('IVA:', '$0.00'),
      payload.totales.totalDescuento > 0 ? derecha('DESCUENTO:', `-$${payload.totales.totalDescuento.toFixed(2)}`) : '',
      linea,
      derecha('TOTAL:', `$${payload.totales.valorPagar.toFixed(2)}`),
      linea,
      efectivoRecibido ? derecha('EFECTIVO:', `$${parseFloat(efectivoRecibido).toFixed(2)}`) : '',
      efectivoRecibido ? derecha('CAMBIO:', `$${Math.max(0, parseFloat(efectivoRecibido) - payload.totales.valorPagar).toFixed(2)}`) : '',
      '',
      claveAcceso ? 'Clave de Acceso:' : '',
      claveAcceso ? claveAcceso : '',
      '',
      centrar(configImp.mensaje_pie_ticket || 'Gracias por su compra'),
      '',
    ].filter(Boolean).join('\n');

    abrirVentanaTicket(ticket, tipo, configImp);
  };

  // Helper para abrir ventana de impresión
  const abrirVentanaTicket = (ticket, tipo, configImp) => {
    const ventana = window.open('', '_blank', `width=${tipo === '58mm' ? 220 : 302},height=600`);
    if (!ventana) return;
    ventana.document.write(`<!DOCTYPE html><html><head><title>Ticket</title>
      <style>
        @page { margin: 0; size: ${tipo} auto; }
        * { margin: 0; padding: 0; }
        body { font-family: 'Courier New', monospace; font-size: ${tipo === '58mm' ? '9px' : '11px'}; 
          padding: ${configImp.margen_ticket || 3}px; width: ${tipo === '58mm' ? '48mm' : '72mm'}; }
        pre { white-space: pre-wrap; word-wrap: break-word; }
      </style></head><body><pre>${ticket}</pre>
      <script>window.onload=function(){window.print();setTimeout(function(){window.close()},1500);}</script>
    </body></html>`);
    ventana.document.close();
  };

  // Cobrar (Factura SRI)
  const cobrar = async () => {
    if (cart.length === 0) return;
    setProcesando(true);
    setMensaje({ text: '', tipo: '' });

    const payload = {
      cliente: {
        nombre: cliente.nombre || 'CONSUMIDOR FINAL',
        ruc: cliente.ruc || '9999999999999',
        direccion: cliente.direccion || 'S/N',
        email: cliente.email || '',
      },
      detalles: cart.map(c => ({
        codigo: c.codigo,
        nombre: c.nombre,
        cantidad: c.cantidad,
        precio: c.precio,
        iva: c.iva,
        descuento: c.cantidad * c.precio * (c.descuento / 100),
      })),
      totales: {
        subtotalSinImpuestos: subtotal0 + subtotal15,
        subtotal15, subtotal0,
        totalDescuento: 0,
        iva15: iva,
        valorPagar: total,
      },
      pago: { forma: formaPago, valor: total },
      puntoEmisionSeleccionado: {
        establecimiento: config.establecimiento,
        puntoEmision: config.puntoEmision,
      },
    };

    try {
      // Registrar o actualizar cliente automáticamente (si no es consumidor final)
      if (cliente.ruc && cliente.ruc !== '9999999999999' && cliente.nombre) {
        try {
          await axios.post(`${API_URL}/api/clientes`, {
            identificacion: cliente.ruc,
            razon_social: cliente.nombre,
            nombre: cliente.nombre,
            direccion_1: cliente.direccion || 'S/N',
            emails: cliente.email ? [cliente.email] : [],
            tipo_identificacion: cliente.ruc.length === 13 ? 'RUC' : cliente.ruc.length === 10 ? 'CEDULA' : 'PASAPORTE',
          }, { headers: headers() });
        } catch (errCli) {
          // 409 = ya existe → intentar actualizar datos
          if (errCli.response?.status === 409) {
            try {
              // Buscar el cliente existente para obtener su ID
              const resCli = await axios.get(
                `${API_URL}/api/clientes/buscar/identificacion/${cliente.ruc}`,
                { headers: headers() }
              );
              if (resCli.data?.id) {
                await axios.put(`${API_URL}/api/clientes/${resCli.data.id}`, {
                  razon_social: cliente.nombre,
                  nombre: cliente.nombre,
                  direccion_1: cliente.direccion || resCli.data.direccion_1 || 'S/N',
                  emails: cliente.email ? [cliente.email] : (resCli.data.emails || []),
                }, { headers: headers() });
              }
            } catch {
              // Actualización fallida — no bloquear la venta
            }
          }
          // Otros errores se ignoran para no bloquear la facturación
        }
      }

      const res = await axios.post(`${API_URL}/api/factura`, payload, { headers: headers() });
      if (res.data.success !== false) {
        setUltimaFactura(res.data);
        setMensaje({ text: `✅ Factura ${res.data.secuencial || ''} emitida`, tipo: 'success' });

        // Imprimir ticket automáticamente si está configurado
        const configImpresora = JSON.parse(localStorage.getItem('config_impresora') || '{}');
        if (configImpresora.auto_imprimir_ticket) {
          imprimirTicket(payload, res.data, configImpresora);
        }

        setCart([]);
        setCliente({ nombre: 'CONSUMIDOR FINAL', ruc: '9999999999999', direccion: 'S/N', email: '' }); setCreditoInfo(null);
        setEfectivoRecibido('');
        setShowCobrar(false);
        setTimeout(() => { setMensaje({ text: '', tipo: '' }); searchRef.current?.focus(); }, 4000);
      } else {
        setMensaje({ text: `❌ ${res.data.error || 'Error'}`, tipo: 'error' });
      }
    } catch (err) {
      setMensaje({ text: `❌ ${err.response?.data?.error || err.message}`, tipo: 'error' });
    }
    setProcesando(false);
  };

  // Venta en espera
  const enviarAEspera = () => {
    if (cart.length === 0) return;
    setVentasEspera([...ventasEspera, { cart: [...cart], cliente: { ...cliente }, fecha: new Date() }]);
    setCart([]);
    setCliente({ nombre: 'CONSUMIDOR FINAL', ruc: '9999999999999', direccion: 'S/N', email: '' }); setCreditoInfo(null);
    setMensaje({ text: '⏳ Venta enviada a espera', tipo: 'info' });
    setTimeout(() => setMensaje({ text: '', tipo: '' }), 2000);
  };

  const recuperarDeEspera = (index) => {
    const venta = ventasEspera[index];
    setCart(venta.cart);
    setCliente(venta.cliente);
    setVentasEspera(ventasEspera.filter((_, i) => i !== index));
    setShowEspera(false);
  };

  const anularVenta = () => {
    if (cart.length === 0) return;
    if (window.confirm('¿Anular la venta actual?')) {
      setCart([]);
      setCliente({ nombre: 'CONSUMIDOR FINAL', ruc: '9999999999999', direccion: 'S/N', email: '' }); setCreditoInfo(null);
      searchRef.current?.focus();
    }
  };

  const salirPOS = () => navigate('/');

  const temaPOS = localStorage.getItem('tema_pos') || JSON.parse(localStorage.getItem('config_impresora') || '{}').tema_pos || 'tema-azul';

  return (
    <div className={`pos-container ${temaPOS}`}>
      {/* HEADER */}
      <div className="pos-header">
        <div className="pos-header-left">
          <span className="pos-empresa">{empresa.nombre || 'PUNTO DE VENTA'}</span>
          <span className="pos-caja">CAJA {config.puntoEmision}</span>
        </div>
        <div className="pos-header-right">
          <span className="pos-cliente-label">
            {cliente.nombre}
            {creditoInfo && <span style={{ marginLeft: 8, fontSize: 11, color: creditoInfo.disponible > 0 ? '#00e676' : '#ff5252' }}>
              [Crédito: ${creditoInfo.disponible.toFixed(2)}]
            </span>}
          </span>
          <button className="pos-btn-salir" onClick={salirPOS}>SALIR</button>
        </div>
      </div>

      {/* MENSAJE */}
      {mensaje.text && (
        <div className={`pos-mensaje pos-mensaje-${mensaje.tipo}`}>{mensaje.text}</div>
      )}

      {/* BODY */}
      <div className="pos-body">
        {/* PANEL IZQUIERDO: búsqueda + carrito */}
        <div className="pos-left">
          {/* Buscador */}
          <div className="pos-search-box">
            <input
              ref={searchRef}
              className="pos-search-input"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              onKeyDown={handleBusquedaKeyDown}
              placeholder="🔍 Buscar producto, código o [F1]"
              autoFocus
            />
            {resultados.length > 0 && (
              <div className="pos-search-results">
                {resultados.map((p, i) => (
                  <div key={p.id || i} className="pos-search-item" onClick={() => solicitarCantidad(p)}>
                    <span className="pos-search-item-code">{p.codigo || 'S/C'}</span>
                    <span className="pos-search-item-name">{p.nombre}</span>
                    <span className="pos-search-item-price">${parseFloat(p.pvp || p.precio_1 || 0).toFixed(2)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Carrito */}
          <div className="pos-cart">
            <div className="pos-cart-header">
              <span>Cant</span>
              <span>Producto</span>
              <span>P.Unit</span>
              <span>Total</span>
              <span></span>
            </div>
            <div className="pos-cart-items">
              {cart.length === 0 ? (
                <div className="pos-cart-empty">Escanee o busque un producto para comenzar</div>
              ) : (
                cart.map((item, i) => (
                  <div key={i} className="pos-cart-row">
                    <div className="pos-cart-qty">
                      <button onClick={() => cambiarCantidad(i, -1)}>−</button>
                      <span>{item.cantidad}</span>
                      <button onClick={() => cambiarCantidad(i, 1)}>+</button>
                    </div>
                    <span className="pos-cart-name">{item.nombre}</span>
                    <span className="pos-cart-price">
                      <select value={item.precio} onChange={(e) => {
                        const nuevo = [...cart];
                        nuevo[i].precio = parseFloat(e.target.value);
                        setCart(nuevo);
                      }} style={{ background: 'transparent', border: '1px solid #444', color: '#fff', borderRadius: 4, padding: '2px 4px', fontSize: 12, cursor: 'pointer' }}>
                        <option value={item.pvp} style={{ background: '#1a2940' }}>PVP ${item.pvp?.toFixed(2)}</option>
                        {item.precio_2 > 0 && <option value={item.precio_2} style={{ background: '#1a2940' }}>P2 ${item.precio_2.toFixed(2)}</option>}
                        {item.precio_3 > 0 && <option value={item.precio_3} style={{ background: '#1a2940' }}>P3 ${item.precio_3.toFixed(2)}</option>}
                      </select>
                    </span>
                    <span className="pos-cart-total">${(item.cantidad * item.precio).toFixed(2)}</span>
                    <button className="pos-cart-delete" onClick={() => eliminarItem(i)}>✕</button>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* PANEL DERECHO: totales */}
        <div className="pos-right">
          <div className="pos-total-big">
            <span className="pos-total-label">TOTAL</span>
            <span className="pos-total-value">${total.toFixed(2)}</span>
          </div>
          <div className="pos-totales-detalle">
            <div className="pos-totales-row"><span>Subtotal:</span><span>${(subtotal0 + subtotal15).toFixed(2)}</span></div>
            {subtotal0 > 0 && <div className="pos-totales-row"><span>  IVA 0%:</span><span>${subtotal0.toFixed(2)}</span></div>}
            {subtotal15 > 0 && <div className="pos-totales-row"><span>  IVA 15%:</span><span>${subtotal15.toFixed(2)}</span></div>}
            <div className="pos-totales-row"><span>IVA:</span><span>${iva.toFixed(2)}</span></div>
            <div className="pos-totales-row pos-totales-items"><span>Items:</span><span>{cart.reduce((s, c) => s + c.cantidad, 0)}</span></div>
          </div>

          {/* Última factura */}
          {ultimaFactura && (
            <div className="pos-ultima-factura">
              Última: {ultimaFactura.secuencial || 'OK'} ✓
            </div>
          )}
        </div>
      </div>

      {/* FOOTER: teclas rápidas */}
      <div className="pos-footer">
        <button className="pos-foot-btn pos-foot-cobrar" onClick={() => cart.length > 0 && setShowCobrar(true)} disabled={cart.length === 0}>
          <span className="pos-foot-key">[F5]</span> Cobrar
        </button>
        <button className="pos-foot-btn" onClick={() => { setClienteModal({ nombre: '', ruc: '', direccion: '', email: '' }); setClienteEncontrado(false); setShowCliente(true); }}>
          <span className="pos-foot-key">[F2]</span> Cliente
        </button>
        <button className="pos-foot-btn" onClick={() => cart.length > 0 ? enviarAEspera() : setShowEspera(true)}>
          <span className="pos-foot-key">[F9]</span> Espera {ventasEspera.length > 0 && `(${ventasEspera.length})`}
        </button>
        <button className="pos-foot-btn pos-foot-anular" onClick={anularVenta} disabled={cart.length === 0}>
          <span className="pos-foot-key">[F12]</span> Anular
        </button>
      </div>

      {/* MODAL CANTIDAD */}
      {productoParaAgregar && (
        <div className="pos-modal-overlay" onClick={() => { setProductoParaAgregar(null); setStockError(''); searchRef.current?.focus(); }}>
          <div className="pos-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 380 }}>
            <h2 style={{ fontSize: 16, marginBottom: 8 }}>📦 Cantidad</h2>
            <div style={{ background: '#0d1b2a', borderRadius: 8, padding: 12, marginBottom: 12 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#fff' }}>{productoParaAgregar.nombre}</div>
              <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
                Código: {productoParaAgregar.codigo || 'S/C'} — Precio: ${parseFloat(productoParaAgregar.pvp || productoParaAgregar.precio_1 || 0).toFixed(2)}
              </div>
              <div style={{ fontSize: 12, marginTop: 6, color: parseFloat(productoParaAgregar.cantidad_stock || 0) > 0 ? '#00e676' : '#ff5252', fontWeight: 600 }}>
                Stock disponible: {parseFloat(productoParaAgregar.cantidad_stock || productoParaAgregar.stock_actual || 0)}
              </div>
            </div>
            <label style={{ fontSize: 12, color: '#888', fontWeight: 600 }}>¿Cuántas unidades?</label>
            <input
              ref={cantidadRef}
              type="number"
              className="pos-cobrar-input"
              value={cantidadInput}
              onChange={(e) => { setCantidadInput(e.target.value); setStockError(''); }}
              onKeyDown={(e) => { if (e.key === 'Enter') { if (productoParaAgregar.producto_especial && !precioCustom) { document.getElementById('precio-custom-input')?.focus(); } else { confirmarAgregarProducto(); } } if (e.key === 'Escape') { setProductoParaAgregar(null); setStockError(''); searchRef.current?.focus(); } }}
              min="0.01"
              step="1"
              autoFocus
              style={{ marginTop: 8 }}
            />
            {/* Precio modificable para producto especial */}
            {productoParaAgregar.producto_especial && (
              <div style={{ marginTop: 12 }}>
                <div style={{ fontSize: 11, color: '#ff9800', fontWeight: 600, marginBottom: 4 }}>⚡ PRODUCTO ESPECIAL — Precio modificable</div>
                <div style={{ fontSize: 11, color: '#888', marginBottom: 6 }}>Precio por defecto: <strong>${parseFloat(productoParaAgregar.pvp || productoParaAgregar.precio_1 || 0).toFixed(2)}</strong></div>
                <input
                  id="precio-custom-input"
                  type="number"
                  className="pos-cobrar-input"
                  value={precioCustom}
                  onChange={(e) => setPrecioCustom(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') confirmarAgregarProducto(); }}
                  placeholder={parseFloat(productoParaAgregar.pvp || productoParaAgregar.precio_1 || 0).toFixed(2)}
                  min="0.01"
                  step="0.01"
                  style={{ fontSize: 18 }}
                />
              </div>
            )}
            {stockError && (
              <div style={{ marginTop: 10, padding: '8px 12px', borderRadius: 6, background: '#b71c1c', color: '#ffcdd2', fontSize: 12, fontWeight: 600 }}>
                {stockError}
              </div>
            )}
            {!stockError && cantidadInput && parseFloat(cantidadInput) > 0 && (
              <div style={{ textAlign: 'center', marginTop: 12, fontSize: 13, color: '#a5d6a7' }}>
                Subtotal: <strong>${(parseFloat(cantidadInput) * parseFloat(precioCustom || productoParaAgregar.pvp || productoParaAgregar.precio_1 || 0)).toFixed(2)}</strong>
              </div>
            )}
            <div className="pos-cobrar-actions" style={{ marginTop: 16 }}>
              <button className="pos-btn-cancelar" onClick={() => { setProductoParaAgregar(null); setStockError(''); searchRef.current?.focus(); }}>Cancelar</button>
              <button className="pos-btn-confirmar" onClick={confirmarAgregarProducto}>✅ AGREGAR</button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL COBRAR */}
      {showCobrar && (
        <div className="pos-modal-overlay" onClick={() => setShowCobrar(false)}>
          <div className="pos-modal pos-modal-cobrar" onClick={e => e.stopPropagation()}>
            <h2>💰 Cobrar Venta</h2>
            <div className="pos-cobrar-total">
              <span>TOTAL A COBRAR</span>
              <span className="pos-cobrar-monto">${total.toFixed(2)}</span>
            </div>
            <div className="pos-cobrar-form">
              <label>Forma de Pago</label>
              <select value={formaPago} onChange={(e) => setFormaPago(e.target.value)}>
                <option value="01">💵 Efectivo</option>
                <option value="16">💳 Tarjeta de débito</option>
                <option value="19">💳 Tarjeta de crédito</option>
                <option value="17">📱 Dinero electrónico</option>
                <option value="20">🏦 Transferencia</option>
                <option value="credito_plazo">📋 Crédito (a plazo)</option>
              </select>
              {formaPago === '01' && (
                <>
                  <label>Efectivo Recibido</label>
                  <input
                    ref={efectivoRef}
                    type="number"
                    className="pos-cobrar-input"
                    value={efectivoRecibido}
                    onChange={(e) => setEfectivoRecibido(e.target.value)}
                    placeholder="0.00"
                    autoFocus
                    onKeyDown={(e) => { if (e.key === 'Enter') cobrar(); }}
                  />
                  <div className={`pos-cobrar-cambio ${cambio >= 0 ? 'positivo' : 'negativo'}`}>
                    <span>Cambio:</span>
                    <span className="pos-cambio-valor">${cambio >= 0 ? cambio.toFixed(2) : '0.00'}</span>
                  </div>
                </>
              )}
            </div>
            {/* Alerta de crédito */}
            {creditoInfo && creditoInfo.disponible < total && (
              <div style={{ padding: '10px 12px', borderRadius: 6, background: '#b71c1c', color: '#ffcdd2', fontSize: 12, fontWeight: 600, marginTop: 10 }}>
                ❌ Crédito insuficiente. Disponible: ${creditoInfo.disponible.toFixed(2)} | Límite: ${creditoInfo.limite.toFixed(2)} | Deuda: ${creditoInfo.saldoPendiente.toFixed(2)}
              </div>
            )}
            {creditoInfo && creditoInfo.disponible >= total && (
              <div style={{ padding: '8px 12px', borderRadius: 6, background: '#1b5e20', color: '#a5d6a7', fontSize: 12, marginTop: 10 }}>
                ✅ Crédito disponible: ${creditoInfo.disponible.toFixed(2)} (Límite: ${creditoInfo.limite.toFixed(2)})
              </div>
            )}
            <div className="pos-cobrar-actions" style={{ flexDirection: 'column', gap: 8 }}>
              {recibosHabilitado && (
                <button className="pos-btn-confirmar" onClick={emitirRecibo}
                  disabled={procesando || (creditoInfo && creditoInfo.disponible < total)}
                  style={{ background: '#e65100', flex: 'none' }}>
                  {procesando ? '⏳...' : '🧾 RECIBO (sin factura)'}
                </button>
              )}
              <div style={{ display: 'flex', gap: 12 }}>
                <button className="pos-btn-cancelar" onClick={() => setShowCobrar(false)}>Cancelar</button>
                <button className="pos-btn-confirmar" onClick={cobrar}
                  disabled={procesando || (formaPago === '01' && cambio < 0) || (creditoInfo && creditoInfo.disponible < total)}>
                  {procesando ? '⏳ Procesando...' : '✅ FACTURAR SRI'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MODAL CLIENTE */}
      {showCliente && (
        <div className="pos-modal-overlay" onClick={() => setShowCliente(false)}>
          <div className="pos-modal" onClick={e => e.stopPropagation()}>
            <h2>👤 Datos del Cliente</h2>
            <div className="pos-cliente-form">
              <label>CI / RUC</label>
              <div style={{ position: 'relative' }}>
                <input id="cli-ruc" value={clienteModal.ruc}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '');
                    setClienteModal({ ...clienteModal, ruc: val });
                    setClienteEncontrado(false);
                    if (val.length === 10 || val.length === 13) {
                      buscarClientePorCedula(val);
                    }
                  }}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); document.getElementById('cli-nombre').focus(); } }}
                  placeholder="Ingrese cédula o RUC..." maxLength={13} autoFocus />
                {buscandoCli && (
                  <span style={{ position: 'absolute', right: 12, top: 12, color: '#00e676' }}>🔄 Buscando...</span>
                )}
                {clienteEncontrado && (
                  <span style={{ position: 'absolute', right: 12, top: 12, color: '#00e676' }}>✓ Encontrado</span>
                )}
              </div>
              <label>Nombre / Razón Social</label>
              <input id="cli-nombre" value={clienteModal.nombre} onChange={(e) => setClienteModal({ ...clienteModal, nombre: e.target.value.toUpperCase() })}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); document.getElementById('cli-direccion').focus(); } }}
                placeholder="Nombre del cliente" />
              <label>Dirección</label>
              <input id="cli-direccion" value={clienteModal.direccion} onChange={(e) => setClienteModal({ ...clienteModal, direccion: e.target.value })}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); document.getElementById('cli-email').focus(); } }}
                placeholder="Dirección" />
              <label>Email</label>
              <input id="cli-email" value={clienteModal.email} onChange={(e) => setClienteModal({ ...clienteModal, email: e.target.value })}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); if (clienteModal.ruc && clienteModal.nombre) { setCliente({ ...clienteModal, direccion: clienteModal.direccion || 'S/N' }); } setShowCliente(false); } }}
                placeholder="email@ejemplo.com" />
            </div>
            <div className="pos-cobrar-actions">
              <button className="pos-btn-cancelar" onClick={() => {
                setCliente({ nombre: 'CONSUMIDOR FINAL', ruc: '9999999999999', direccion: 'S/N', email: '' }); setCreditoInfo(null);
                setClienteEncontrado(false);
                setCreditoInfo(null);
                setShowCliente(false);
              }}>Consumidor Final</button>
              <button className="pos-btn-confirmar" onClick={() => {
                if (clienteModal.ruc && clienteModal.nombre) {
                  setCliente({ ...clienteModal, direccion: clienteModal.direccion || 'S/N' });
                }
                setShowCliente(false);
              }}>✅ Guardar</button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL ESPERA */}
      {showEspera && (
        <div className="pos-modal-overlay" onClick={() => setShowEspera(false)}>
          <div className="pos-modal" onClick={e => e.stopPropagation()}>
            <h2>⏳ Ventas en Espera ({ventasEspera.length})</h2>
            {ventasEspera.length === 0 ? (
              <p style={{ textAlign: 'center', opacity: 0.6 }}>No hay ventas en espera</p>
            ) : (
              <div className="pos-espera-list">
                {ventasEspera.map((v, i) => (
                  <div key={i} className="pos-espera-item" onClick={() => recuperarDeEspera(i)}>
                    <div>
                      <strong>{v.cliente.nombre}</strong>
                      <span>{v.cart.length} items</span>
                    </div>
                    <span className="pos-espera-total">
                      ${v.cart.reduce((s, c) => s + c.cantidad * c.precio, 0).toFixed(2)}
                    </span>
                  </div>
                ))}
              </div>
            )}
            <div className="pos-cobrar-actions">
              <button className="pos-btn-cancelar" onClick={() => setShowEspera(false)}>Cerrar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
